import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { LangLink } from "@/components/LangLink";
import { ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { getContentBrand, CATEGORY_NAV } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { AeoAnswerBlock } from "@/components/aeo/AeoAnswerBlock";
import { AeoFaq } from "@/components/aeo/AeoFaq";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

const PAGE = 24;

export default function BrandPage() {
  const { slug } = useParams();
  const { lang, tx } = useI18n();
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = useCallback(async (offset = 0, reset = true) => {
    reset ? setLoading(true) : setLoadingMore(true);
    try {
      const d = await getContentBrand(slug, { limit: PAGE, offset, lang });
      setMeta(d);
      setProducts((prev) => (reset ? d.products : [...prev, ...d.products]));
      setTotal(d.total || 0);
      setHasNext(d.has_next);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    } finally {
      reset ? setLoading(false) : setLoadingMore(false);
    }
  }, [slug, lang]);

  useEffect(() => { window.scrollTo({ top: 0 }); load(0, true); }, [slug, lang, load]);

  if (notFound) {
    return (
      <div className="container-site py-24 text-center">
        <h1 className="font-display text-3xl">{tx("Brand not found")}</h1>
        <Button asChild className="mt-6"><LangLink to="/">{tx("Back to home")}</LangLink></Button>
      </div>
    );
  }

  return (
    <>
      {meta && <Seo title={meta.name} description={`Shop the full ${meta.name} range at Boomerang.ie, an official Irish reseller. ${meta.tagline} Ireland & EU shipping.`} path={`/brand/${slug}`} />}

      <div className="section-navy grain relative overflow-hidden">
        <img src="/kentucky/life3.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--brand-navy))] to-[hsl(var(--brand-navy))]/70" />
        <div className="container-site relative py-14 lg:py-20">
          <Breadcrumb className="mb-5">
            <BreadcrumbList className="text-white/60">
              <BreadcrumbItem><BreadcrumbLink asChild><LangLink to="/" className="text-white/60 hover:text-white">Home</LangLink></BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage className="text-white">{meta?.name || slug}</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {loading && !meta ? <Skeleton className="h-12 w-72 bg-white/10" /> : (
            <>
              <p className="eyebrow text-[hsl(var(--primary))]">{tx("Maison")}</p>
              {meta?.logo && <img src={meta.logo} alt={meta.name} className="mt-3 h-10 w-auto object-contain brightness-0 invert" />}
              <h1 className="mt-3 font-display text-4xl text-white sm:text-6xl">{meta?.name}</h1>
              <div className="mt-5 h-px w-24 bg-[hsl(var(--primary))]" />
              <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-white/80">{meta?.intro}</p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                {total > 0 && <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-white/85">{total} {tx("products")}</span>}
                {meta?.shop_all_url && (
                  <Button asChild variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="brand-shop-all">
                    <a href={meta.shop_all_url} target="_blank" rel="noopener noreferrer">{tx("Shop all on Boomerang.ie")} <ExternalLink className="ml-2 h-4 w-4" /></a>
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <section className="container-site py-10">
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (<div key={i}><Skeleton className="aspect-[4/5] w-full rounded-lg" /><Skeleton className="mt-3 h-4 w-2/3" /></div>))}
          </div>
        ) : products.length ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4" data-testid="product-grid">
              {products.map((p) => <ProductCard key={p.handle} product={p} />)}
            </div>
            {hasNext && (
              <div className="mt-10 flex justify-center">
                <Button variant="outline" size="lg" onClick={() => load(products.length, false)} disabled={loadingMore} data-testid="load-more-button">
                  {loadingMore ? tx("Loading…") : `${tx("Load more")} (${total - products.length})`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-secondary/50 p-12 text-center">
            <p className="font-display text-xl">{tx("Explore")} {meta?.name} {tx("on Boomerang.ie")}</p>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">{tx("Browse the full range with live stock and pricing.")}</p>
            {meta?.shop_all_url && <Button asChild className="mt-5"><a href={meta.shop_all_url} target="_blank" rel="noopener noreferrer">{tx("Shop")} {meta?.name}</a></Button>}
          </div>
        )}

        <div className="mt-12 rounded-2xl border border-border bg-secondary/40 p-6">
          <h2 className="font-display text-xl">{tx("Shop by category")}</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {CATEGORY_NAV.map((c) => (
              <LangLink key={c.slug} to={`/collection/${c.slug}`}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium hover:border-boomerang hover:text-boomerang">{tx(c.name)}</LangLink>
            ))}
          </div>
        </div>
      </section>

      {/* AEO ANSWERS \u2014 navy section */}
      {meta && (
        <section data-testid="brand-aeo-section" className="section-navy grain relative">
          <div className="container-site py-16 lg:py-20">
            <p className="eyebrow text-[hsl(var(--primary))]">{tx("Ask anything")}</p>
            <h2 className="mt-3 max-w-2xl font-display text-3xl leading-tight text-white sm:text-4xl">{tx("What riders and AI assistants ask about")} {meta.name}</h2>
            <div className="mt-9 grid gap-4 lg:grid-cols-2">
              <AeoAnswerBlock
                question={`Is Boomerang.ie an official ${meta.name} reseller?`}
                answer={`Yes. Boomerang.ie Horse & Country is an official ${meta.name} reseller, stocking the authentic range with full manufacturer warranty and express worldwide shipping from our Ireland and EU warehouses.`}
              />
              <AeoAnswerBlock
                question={`Does Boomerang.ie ship ${meta.name} worldwide?`}
                answer={`Yes. ${meta.name} orders are dispatched worldwide with express tracked couriers (DPD, DHL, UPS, An Post). Irish orders over \u20ac100 ship free, with Klarna and Humm available at checkout.`}
              />
            </div>
            <div className="mt-10 rounded-2xl bg-card p-6 sm:p-8">
              <AeoFaq
                title={`${meta.name} frequently asked questions`}
                faqs={[
                  { q: `What is ${meta.name} known for?`, a: meta.intro || `${meta.name} is a premium equestrian brand curated by Boomerang.ie, an official reseller shipping worldwide from Ireland and the EU.` },
                  { q: `Are ${meta.name} products authentic?`, a: `Yes. Every ${meta.name} item sold by Boomerang.ie is genuine, sourced through official channels and backed by full manufacturer warranty.` },
                  { q: `How long does delivery take?`, a: `Most EU destinations receive ${meta.name} orders within 2 to 4 working days. Worldwide express delivery times and any duties are shown at checkout.` },
                ]}
              />
            </div>
          </div>
        </section>
      )}
    </>
  );
}
